import http from './http';
import { request } from './http';

export interface Result<T> {
  success: boolean;
  message?: string;
  data: T;
}

/** 聊天附件上传结果：脱敏访问 Token 与 URL */
export interface ChatFileUploadResult {
  token: string;
  accessUrl: string;
  fileName: string;
  objectKey?: string;
  processingName?: string;
  processingObjectKey?: string;
  processingAccessUrl?: string;
}

/** 项目文档上传结果 */
export interface ProjectFileUploadResult {
  token: string;
  accessUrl: string;
  fileName: string;
  storedPath: string;
}

/** 需求台账附件上传结果 */
export interface RequirementFileUploadResult {
  token: string;
  accessUrl: string;
  fileName: string;
  storedPath: string;
}

/** 通用部件库附件上传结果 */
export interface ComponentFileUploadResult {
  token: string;
  accessUrl: string;
  fileName: string;
  storedPath: string;
}

/** 项目文档树节点 */
export interface FileTreeNode {
  path: string;
  directory: boolean;
}

/** 轮询聊天附件图纸智审结果 */
export async function pollChatDrawingReviewResult(
  taskId: string,
  intervalMs = 1500,
  onProgress?: (data: import('./aiReviewApi').DesignReviewResultData) => void,
): Promise<import('./aiReviewApi').DesignReviewResultData> {
  for (;;) {
    const wrapped = await getChatDrawingReviewResult(taskId);
    if (!wrapped.success || !wrapped.data) {
      throw new Error(wrapped.message ?? '轮询失败');
    }
    const d = wrapped.data;
    onProgress?.(d);
    if (d.status === 'failed') {
      throw new Error(d.message ?? '评审失败');
    }
    if (d.status === 'completed') {
      return d;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

/** 发起聊天附件图纸智审（temp/chat 临时文件；优先传 accessUrl 由后端解析 objectKey） */
export async function submitChatDrawingReviewTask(
  payload: { accessUrl?: string; objectKey?: string },
  documentType?: string,
  options?: string,
): Promise<{ taskId: string }> {
  const body = new URLSearchParams();
  if (payload.accessUrl) body.set('accessUrl', payload.accessUrl);
  if (payload.objectKey) body.set('objectKey', payload.objectKey);
  if (!payload.accessUrl && !payload.objectKey) {
    throw new Error('缺少附件标识');
  }
  if (documentType) body.set('documentType', documentType);
  if (options) body.set('options', options);
  const res = await request<Result<{ taskId: string }>>(
    http.post('/files/chat/drawing-review/task', body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      timeout: 120000,
    }),
  );
  const r = res as unknown as Result<{ taskId: string }>;
  if (!r.success || !r.data?.taskId) throw new Error(r.message ?? '提交图纸智审失败');
  return r.data;
}

export async function getChatDrawingReviewResult(
  taskId: string,
): Promise<Result<import('./aiReviewApi').DesignReviewResultData>> {
  const res = await request<Result<import('./aiReviewApi').DesignReviewResultData>>(
    http.get('/files/chat/drawing-review/result', { params: { taskId }, timeout: 120000 }),
  );
  return res as Result<import('./aiReviewApi').DesignReviewResultData>;
}

/** 上传聊天附件（临时存储，返回脱敏链接供 Agent 使用） */
export async function uploadChatFile(
  file: File,
  sessionId?: string
): Promise<ChatFileUploadResult> {
  const form = new FormData();
  form.append('file', file);
  if (sessionId) form.append('sessionId', sessionId);
  const res = await request<Result<ChatFileUploadResult>>(
    http.post('/files/upload/chat', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    })
  );
  const result = res as unknown as Result<ChatFileUploadResult>;
  if (!result.success || !result.data) throw new Error(result.message ?? '上传失败');
  return result.data;
}

/** 上传项目文件到存储树（按项目/阶段目录存储） */
export async function uploadProjectPhaseFile(
  file: File,
  projectId: number,
  options?: { phaseCode?: string; relativePath?: string }
): Promise<ProjectFileUploadResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('projectId', String(projectId));
  if (options?.phaseCode) form.append('phaseCode', options.phaseCode);
  if (options?.relativePath) form.append('relativePath', options.relativePath);
  const res = await request<Result<ProjectFileUploadResult>>(
    http.post('/files/upload/project', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  );
  return (res as unknown as Result<ProjectFileUploadResult>).data;
}

/** 上传需求台账附件（长期存储 requirements/ 前缀） */
export async function uploadRequirementDocument(file: File): Promise<RequirementFileUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await request<Result<RequirementFileUploadResult>>(
    http.post('/files/upload/requirement', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    })
  );
  const r = res as unknown as Result<RequirementFileUploadResult>;
  if (!r.success || !r.data) throw new Error(r.message ?? '上传失败');
  return r.data;
}

/** 上传通用部件库附件（长期存储 components/ 前缀） */
export async function uploadComponentDocument(file: File): Promise<ComponentFileUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await request<Result<ComponentFileUploadResult>>(
    http.post('/files/upload/component', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    })
  );
  const r = res as unknown as Result<ComponentFileUploadResult>;
  if (!r.success || !r.data) throw new Error(r.message ?? '上传失败');
  return r.data;
}

export async function getAccessUrlByObjectKey(objectKey: string): Promise<{ accessUrl: string; token: string; fileName: string }> {
  const res = await request<Result<{ accessUrl: string; token: string; fileName: string }>>(
    http.get('/files/access-url', { params: { objectKey } })
  );
  const r = res as unknown as Result<{ accessUrl: string; token: string; fileName: string }>;
  if (!r.success || !r.data) throw new Error(r.message ?? '获取链接失败');
  return r.data;
}

/** 获取项目文档树（按项目、可选阶段） */
export async function getProjectFileTree(
  projectId: number,
  phaseCode?: string
): Promise<FileTreeNode[]> {
  const params: Record<string, string> = {};
  if (phaseCode) params.phaseCode = phaseCode;
  const res = await request<Result<FileTreeNode[]>>(
    http.get('/files/project/' + projectId + '/tree', { params })
  );
  return (res as unknown as Result<FileTreeNode[]>).data;
}

// ---------- 项目详情页：立项阶段统一文档资料 ----------

export interface ProjectDocCategory {
  id: string;
  name: string;
  sortOrder?: number | null;
}

export interface ProjectDocDeletePolicy {
  canDelete: boolean;
  policyType?: string | null;
  confirmMessage?: string | null;
  /** false 表示直接删除，不进入历史版本 */
  moveToHistory?: boolean;
  linkedIssueTitles?: string[];
}

export interface ProjectDocFile {
  id: string;
  objectKey: string;
  fileName: string;
  sourceType: string;
  categoryId: string | null;
  uploadedAt: string;
  /** 内容最近更新时间；无则回退 uploadedAt */
  updatedAt?: string | null;
  logicalId: string;
  accessUrl: string;
  uploadedByUserId?: number | null;
  /** 原附件上传人；分类调整不使用在线版本最后编辑者身份。 */
  categoryOwnerUserId?: number | null;
  uploadedByName?: string | null;
  deletePolicy?: ProjectDocDeletePolicy | null;
  workspaceVersionId?: string | null;
  versionLabel?: string | null;
  requirementAnalysisRevision?: number | null;
  /** APQP 所属阶段编码 */
  stageCode?: string | null;
  /** 文件大小（字节）；用于 10M 在线预览/审查门禁，缺省时不按大小拦截 */
  sizeBytes?: number | null;
}

export interface ProjectDocHistory {
  objectKey: string;
  fileName: string;
  sourceType: string;
  categoryId: string | null;
  uploadedAt: string;
  updatedAt?: string | null;
  removedAt: string;
  logicalId: string;
  accessUrl: string;
  uploadedByUserId?: number | null;
  uploadedByName?: string | null;
  workspaceVersionId?: string | null;
  versionLabel?: string | null;
}

export interface ProjectDocumentsBundle {
  categories: ProjectDocCategory[];
  files: ProjectDocFile[];
  history: ProjectDocHistory[];
  /** 历史版本号（只读模式） */
  historicalVersion?: string | null;
  /** 文档空间列表缓存版本号（服务端 Redis 递增） */
  listVersion?: number | null;
  /** sinceVersion 命中时为 true，正文可为空 */
  unchanged?: boolean | null;
  /** false 表示增量补丁，需与本地缓存合并；默认 true */
  fullReplace?: boolean | null;
  /** 增量：更新/新增的当前文件 */
  upsertFiles?: ProjectDocFile[] | null;
  /** 增量：更新/新增的历史项 */
  upsertHistory?: ProjectDocHistory[] | null;
  /** 增量：已删除的当前文件 objectKey */
  removedObjectKeys?: string[] | null;
  /** 增量：已删除的历史项键 objectKey::workspaceVersionId|uploadedAt */
  removedHistoryKeys?: string[] | null;
}

/** 超过此大小走分片上传，避免完整文件经过网关。 */
export const CHUNKED_UPLOAD_THRESHOLD_BYTES = 8 * 1024 * 1024;

export interface AttachmentUploadSession {
  sessionId: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
  uploadedChunks: number[];
  status: string;
}

export async function listProjectDocuments(
  projectId: number,
  options?: {
    verMajor?: number;
    verMinor?: number;
    metadataOnly?: boolean;
    /** 前端缓存的列表版本；命中则返回 unchanged */
    sinceVersion?: number | null;
    /** 当前页可见 objectKey，用于版本变化时只回传本页更新 */
    pageObjectKeys?: string[] | null;
  },
): Promise<ProjectDocumentsBundle> {
  const params: Record<string, number | boolean | string> = {};
  if (options?.metadataOnly) params.metadataOnly = true;
  if (options?.verMajor != null) params.verMajor = options.verMajor;
  if (options?.verMinor != null) params.verMinor = options.verMinor;
  if (options?.sinceVersion != null && Number.isFinite(options.sinceVersion) && options.sinceVersion > 0) {
    params.sinceVersion = options.sinceVersion;
  }
  if (options?.pageObjectKeys?.length) {
    params.pageObjectKeys = options.pageObjectKeys.filter(Boolean).join(',');
  }
  const res = await request<Result<ProjectDocumentsBundle>>(
    http.get(`/projects/${projectId}/attachments`, { params }),
  );
  const r = res as unknown as Result<ProjectDocumentsBundle>;
  if (!r.success || !r.data) throw new Error(r.message ?? '获取文档列表失败');
  return r.data;
}

export function suggestUploadTimeoutMs(fileSize: number): number {
  // 按约 256KB/s 估算，下限 3 分钟，上限 30 分钟
  const est = Math.ceil((fileSize / (256 * 1024)) * 1000) + 60_000;
  return Math.min(1_800_000, Math.max(180_000, est));
}

export type UploadProjectDocumentOptions = {
  onProgress?: (percent: number) => void;
  timeoutMs?: number;
  sessionId?: string;
  onSession?: (session: AttachmentUploadSession) => void;
  signal?: AbortSignal;
  /** 需求台账等场景；与 scenario 正交 */
  scope?: 'REQUIREMENT';
  issueTaskId?: number;
  /** APQP 所属阶段编码（REQ-06） */
  stageCode?: string | null;
};

/**
 * 兼容两种调用：
 * - (…, categoryId, scenario, options)  — master 可续传 / issue 场景
 * - (…, categoryId, options)            — codeIntegration 的 scope 调用方
 */
function resolveUploadScenarioAndOptions(
  scenarioOrOptions: 'project' | 'issue' | 'wbs' | UploadProjectDocumentOptions = 'project',
  maybeOptions?: UploadProjectDocumentOptions,
): { scenario: 'project' | 'issue' | 'wbs'; options?: UploadProjectDocumentOptions } {
  if (typeof scenarioOrOptions === 'object' && scenarioOrOptions !== null) {
    return { scenario: 'project', options: scenarioOrOptions };
  }
  return { scenario: scenarioOrOptions, options: maybeOptions };
}

export async function uploadProjectDocument(
  projectId: number,
  type: 'PROJECT' | 'CUSTOMER',
  file: File,
  categoryId?: string | null,
  scenarioOrOptions: 'project' | 'issue' | 'wbs' | UploadProjectDocumentOptions = 'project',
  maybeOptions?: UploadProjectDocumentOptions,
): Promise<{ objectKey: string; fileName: string; accessUrl: string }> {
  const { scenario, options } = resolveUploadScenarioAndOptions(scenarioOrOptions, maybeOptions);
  // 大文件走分片，支持进度与断点续传
  if (file.size > CHUNKED_UPLOAD_THRESHOLD_BYTES || options?.sessionId) {
    return uploadProjectDocumentResumable(projectId, type, file, categoryId, scenario, options);
  }
  const form = new FormData();
  form.append('type', type);
  form.append('file', file);
  if (categoryId) form.append('categoryId', categoryId);
  if (options?.stageCode) form.append('stageCode', options.stageCode);
  if (scenario && scenario !== 'project') form.append('scenario', scenario);
  if (options?.scope) form.append('scope', options.scope);
  if (options?.issueTaskId) form.append('issueTaskId', String(options.issueTaskId));
  const res = await request<Result<{ objectKey: string; fileName: string; accessUrl: string }>>(
    http.post(`/projects/${projectId}/attachments/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: options?.timeoutMs ?? suggestUploadTimeoutMs(file.size),
      onUploadProgress: (ev) => {
        const total = ev.total ?? file.size;
        if (total > 0 && options?.onProgress) {
          options.onProgress(Math.min(99, Math.round((ev.loaded / total) * 100)));
        }
      },
    }),
  );
  const r = res as unknown as Result<{ objectKey: string; fileName: string; accessUrl: string }>;
  if (!r.success || !r.data) throw new Error(r.message ?? '上传失败');
  options?.onProgress?.(100);
  return r.data;
}

export async function uploadProjectDocumentsBatch(
  projectId: number,
  type: 'PROJECT' | 'CUSTOMER',
  files: File[],
  categoryId?: string | null,
  scenario: 'project' | 'issue' | 'wbs' = 'project',
  options?: { onProgress?: (percent: number) => void; timeoutMs?: number },
): Promise<Array<{ objectKey: string; fileName: string; accessUrl: string }>> {
  // 逐个上传以便真实进度；大文件自动分片
  const out: Array<{ objectKey: string; fileName: string; accessUrl: string }> = [];
  const totalBytes = files.reduce((s, f) => s + (f.size || 0), 0) || 1;
  let doneBytes = 0;
  for (const file of files) {
    const uploaded = await uploadProjectDocument(projectId, type, file, categoryId, scenario, {
      timeoutMs: options?.timeoutMs,
      onProgress: (p) => {
        if (!options?.onProgress) return;
        const cur = doneBytes + (file.size * p) / 100;
        options.onProgress(Math.min(99, Math.round((cur / totalBytes) * 100)));
      },
    });
    out.push(uploaded);
    doneBytes += file.size;
  }
  options?.onProgress?.(100);
  return out;
}

export async function createAttachmentUploadSession(
  projectId: number,
  body: {
    fileName: string;
    fileSize: number;
    contentType?: string;
    type: 'PROJECT' | 'CUSTOMER';
    scenario?: 'project' | 'issue' | 'wbs';
    categoryId?: string | null;
    stageCode?: string | null;
    scope?: 'REQUIREMENT';
    issueTaskId?: number;
  },
): Promise<AttachmentUploadSession> {
  const res = await request<Result<AttachmentUploadSession>>(
    http.post(`/projects/${projectId}/attachments/upload-sessions`, body, { timeout: 60_000 }),
  );
  const r = res as unknown as Result<AttachmentUploadSession>;
  if (!r.success || !r.data) throw new Error(r.message ?? '创建上传会话失败');
  return r.data;
}

export async function getAttachmentUploadSession(
  projectId: number,
  sessionId: string,
): Promise<AttachmentUploadSession> {
  const res = await request<Result<AttachmentUploadSession>>(
    http.get(`/projects/${projectId}/attachments/upload-sessions/${encodeURIComponent(sessionId)}`, {
      timeout: 60_000,
    }),
  );
  const r = res as unknown as Result<AttachmentUploadSession>;
  if (!r.success || !r.data) throw new Error(r.message ?? '查询上传会话失败');
  return r.data;
}

export async function putAttachmentUploadChunk(
  projectId: number,
  sessionId: string,
  index: number,
  blob: Blob,
  options?: { onProgress?: (percent: number) => void; timeoutMs?: number },
): Promise<AttachmentUploadSession> {
  const res = await request<Result<AttachmentUploadSession>>(
    http.put(
      `/projects/${projectId}/attachments/upload-sessions/${encodeURIComponent(sessionId)}/chunks/${index}`,
      blob,
      {
        timeout: options?.timeoutMs ?? suggestUploadTimeoutMs(blob.size),
        headers: { 'Content-Type': 'application/octet-stream' },
        onUploadProgress: (ev) => {
          const total = ev.total ?? blob.size;
          if (total > 0 && options?.onProgress) {
            options.onProgress(Math.min(100, Math.round((ev.loaded / total) * 100)));
          }
        },
      },
    ),
  );
  const r = res as unknown as Result<AttachmentUploadSession>;
  if (!r.success || !r.data) throw new Error(r.message ?? '分片上传失败');
  return r.data;
}

export async function completeAttachmentUploadSession(
  projectId: number,
  sessionId: string,
): Promise<{ objectKey: string; fileName: string; accessUrl: string }> {
  const res = await request<Result<{ objectKey: string; fileName: string; accessUrl: string }>>(
    http.post(
      `/projects/${projectId}/attachments/upload-sessions/${encodeURIComponent(sessionId)}/complete`,
      {},
      { timeout: 600_000 },
    ),
  );
  const r = res as unknown as Result<{ objectKey: string; fileName: string; accessUrl: string }>;
  if (!r.success || !r.data) throw new Error(r.message ?? '完成上传失败');
  return r.data;
}

export async function abortAttachmentUploadSession(projectId: number, sessionId: string): Promise<void> {
  await request<Result<void>>(
    http.delete(`/projects/${projectId}/attachments/upload-sessions/${encodeURIComponent(sessionId)}`, {
      timeout: 60_000,
    }),
  );
}

/** 分片可续传上传；sessionId 可选（续传时传入已有会话） */
export async function uploadProjectDocumentResumable(
  projectId: number,
  type: 'PROJECT' | 'CUSTOMER',
  file: File,
  categoryId?: string | null,
  scenario: 'project' | 'issue' | 'wbs' = 'project',
  options?: UploadProjectDocumentOptions,
): Promise<{ objectKey: string; fileName: string; accessUrl: string }> {
  let session: AttachmentUploadSession;
  if (options?.sessionId) {
    session = await getAttachmentUploadSession(projectId, options.sessionId);
    if (session.fileName !== file.name || session.fileSize !== file.size) {
      throw new Error('所选文件与未完成上传不匹配，请选择相同文件继续');
    }
  } else {
    session = await createAttachmentUploadSession(projectId, {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type || 'application/octet-stream',
      type,
      scenario,
      categoryId,
      stageCode: options?.stageCode,
      scope: options?.scope,
      issueTaskId: options?.issueTaskId,
    });
  }
  options?.onSession?.(session);

  const uploaded = new Set(session.uploadedChunks || []);
  const total = session.totalChunks;
  const chunkSize = session.chunkSize;

  for (let i = 0; i < total; i++) {
    if (options?.signal?.aborted) throw new Error('上传已取消');
    if (uploaded.has(i)) {
      options?.onProgress?.(Math.round(((i + 1) / total) * 95));
      continue;
    }
    const start = i * chunkSize;
    const end = Math.min(file.size, start + chunkSize);
    const blob = file.slice(start, end);
    await putAttachmentUploadChunk(projectId, session.sessionId, i, blob, {
      onProgress: (p) => {
        const base = i / total;
        const slice = 1 / total;
        options?.onProgress?.(Math.min(95, Math.round((base + slice * (p / 100)) * 95)));
      },
    });
    uploaded.add(i);
    options?.onProgress?.(Math.round(((i + 1) / total) * 95));
  }

  const result = await completeAttachmentUploadSession(projectId, session.sessionId);
  options?.onProgress?.(100);
  return result;
}

export async function createProjectDocCategory(
  projectId: number,
  name: string
): Promise<ProjectDocCategory> {
  const res = await request<Result<ProjectDocCategory>>(
    http.post(`/projects/${projectId}/attachments/categories`, { name })
  );
  const r = res as unknown as Result<ProjectDocCategory>;
  if (!r.success || !r.data) throw new Error(r.message ?? '创建分类失败');
  return r.data;
}

export async function deleteProjectDocCategory(projectId: number, categoryId: string): Promise<void> {
  const res = await request<Result<void>>(
    http.delete(`/projects/${projectId}/attachments/categories/${encodeURIComponent(categoryId)}`)
  );
  const r = res as unknown as Result<void>;
  if (!r.success) throw new Error(r.message ?? '删除分类失败');
}

export async function setProjectDocFileCategory(
  projectId: number,
  objectKey: string,
  categoryId: string | null
): Promise<void> {
  const res = await request<Result<void>>(http.patch(`/projects/${projectId}/attachments/file-category`, { objectKey, categoryId }));
  const r = res as unknown as Result<void>;
  if (!r.success) throw new Error(r.message ?? '更新分类失败');
}

export async function referenceProjectDocHistory(
  projectId: number,
  payload: { objectKey: string; categoryId?: string | null; sourceType?: 'PROJECT' | 'CUSTOMER' }
): Promise<void> {
  const res = await request<Result<void>>(
    http.post(`/projects/${projectId}/attachments/reference-history`, payload)
  );
  const r = res as unknown as Result<void>;
  if (!r.success) throw new Error(r.message ?? '引用失败');
}

export async function removeProjectDocFromCurrent(
  projectId: number,
  type: 'PROJECT' | 'CUSTOMER',
  objectKey: string,
  scope?: 'REQUIREMENT',
  analysisRevision?: number,
): Promise<void> {
  const res = await request<Result<void>>(
    http.delete(`/projects/${projectId}/attachments`, { params: { type, objectKey, scope, analysisRevision } })
  );
  const r = res as unknown as Result<void>;
  if (!r.success) throw new Error(r.message ?? '移出失败');
}

export async function purgeProjectDocHistory(projectId: number, objectKey: string): Promise<void> {
  const res = await request<Result<void>>(
    http.delete(`/projects/${projectId}/attachments/history`, { params: { objectKey } })
  );
  const r = res as unknown as Result<void>;
  if (!r.success) throw new Error(r.message ?? '删除失败');
}

export async function getProjectAttachmentAccessUrl(
  projectId: number,
  objectKey: string,
  purpose: 'preview' | 'download' = 'preview',
): Promise<{ accessUrl: string; fileName: string }> {
  const res = await request<Result<{ accessUrl: string; fileName: string }>>(
    http.get(`/projects/${projectId}/attachments/access-url`, { params: { objectKey, purpose } })
  );
  const r = res as unknown as Result<{ accessUrl: string; fileName: string }>;
  if (!r.success || !r.data) throw new Error(r.message ?? '获取预览链接失败');
  return r.data;
}

/** 旧版 Word（.doc）服务端转 HTML，供文档评审在线预览/编辑 */
export async function getProjectLegacyDocHtml(
  projectId: number,
  objectKey: string,
): Promise<{ html: string; fileName: string }> {
  const res = await request<Result<{ html: string; fileName: string }>>(
    http.get(`/projects/${projectId}/attachments/legacy-doc-html`, {
      params: { objectKey },
      timeout: 120_000,
    }),
  );
  const r = res as unknown as Result<{ html: string; fileName: string }>;
  if (!r.success || !r.data?.html) throw new Error(r.message ?? '旧版 Word 文档解析失败');
  return r.data;
}
